import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global Kaharian Hub Status Dot.
 *
 * 8×8 dot in the top nav that polls every 60s across the 4 paid categories
 * (Image, Video, Music, Avatar) and aggregates into green / amber / red.
 *   green  — all categories available
 *   amber  — ≥1 category reports PROVIDERS_UNAVAILABLE (fail-soft, partial)
 *   red    — all 4 categories unavailable (full outage)
 *
 * On hover (desktop) / tap (mobile), shows a per-category breakdown.
 */
const CATEGORIES = ['IMAGE', 'VIDEO', 'MUSIC', 'TALKING_VIDEO'];
const LABELS = {
  IMAGE: 'Image',
  VIDEO: 'Video',
  MUSIC: 'Music',
  TALKING_VIDEO: 'Avatar',
};

async function probeCategory(category) {
  try {
    const { data } = await axios.get(`${API}/api/creative-studio/health/${category}`, { timeout: 8000 });
    return { category, available: !!data.available, message: data.message || '' };
  } catch {
    // Fail-open: treat probe errors as available so we don't falsely alarm users.
    return { category, available: true, message: '' };
  }
}

export function HubStatusDot() {
  const [statuses, setStatuses] = useState(
    CATEGORIES.map((c) => ({ category: c, available: true, message: '' }))
  );
  const [checkedAt, setCheckedAt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const results = await Promise.all(CATEGORIES.map(probeCategory));
      if (!cancelled) {
        setStatuses(results);
        setCheckedAt(Date.now());
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const { color, ariaLabel, down } = useMemo(() => {
    const downList = statuses.filter((s) => !s.available);
    if (downList.length === 0) return { color: 'bg-emerald-500', ariaLabel: 'All generators online', down: [] };
    if (downList.length === statuses.length) return { color: 'bg-red-500', ariaLabel: 'All generators offline', down: downList };
    return { color: 'bg-amber-500', ariaLabel: 'Partial outage', down: downList };
  }, [statuses]);

  const healthy = statuses.filter((s) => s.available);

  const ageLabel = useMemo(() => {
    if (!checkedAt) return 'Checking…';
    const secs = Math.max(0, Math.floor((Date.now() - checkedAt) / 1000));
    if (secs < 10) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    return `${mins}m ago`;
  }, [checkedAt]);

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            data-testid="hub-status-dot"
            className="relative flex items-center justify-center h-6 w-6 rounded-full hover:bg-white/10 transition-colors"
          >
            <span
              className={`h-2 w-2 rounded-full ${color} shadow-[0_0_6px_currentColor]`}
              data-hub-status={down.length === 0 ? 'ok' : (down.length === statuses.length ? 'down' : 'partial')}
            />
            {/* Subtle pulse when partial/down */}
            {down.length > 0 && (
              <span className={`absolute h-2 w-2 rounded-full ${color} opacity-60 animate-ping`} />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-[240px] p-3 bg-charcoal text-white border-white/10">
          <div className="text-xs font-semibold mb-1.5" data-testid="hub-status-title">
            {ariaLabel}
          </div>
          <div className="space-y-1">
            {statuses.map((s) => (
              <div
                key={s.category}
                className="flex items-center justify-between gap-3 text-[11px]"
                data-testid={`hub-status-row-${s.category.toLowerCase()}`}
              >
                <span className="text-gray-300">{LABELS[s.category] || s.category}</span>
                <span className={`font-medium ${s.available ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {s.available ? 'online' : 'maintenance'}
                </span>
              </div>
            ))}
          </div>
          {down.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-gray-400 leading-relaxed">
              {healthy.length > 0
                ? `${healthy.map((h) => LABELS[h.category]).join(' · ')} still online. No credits charged on failures.`
                : 'All upstream providers temporarily unavailable. No credits will be charged.'}
            </div>
          )}
          <div className="mt-2 text-[10px] text-gray-500">Checked {ageLabel}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default HubStatusDot;
